"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, Phone, PhoneIncoming, PhoneOutgoing, Search, Clock } from "lucide-react";

export interface RecordingPickerCall {
  id: string;
  leadName: string | null;
  phone: string;
  date: string;
  duration: number;
  direction: "inbound" | "outbound";
  recordingUrl: string;
  callSid?: string;
}

export interface RecordingPickerProps {
  onSelect: (call: RecordingPickerCall) => void;
}

// Format phone number to (xxx) xxx-xxxx
function formatPhone(phone: string): string {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  if (cleaned.length === 11 && cleaned.startsWith("1")) return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  return phone;
}

// Format seconds to M:SS
function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Format date to relative (Today, Yesterday) or short date
function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return `Today ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export function RecordingPicker({ onSelect }: RecordingPickerProps) {
  const [search, setSearch] = useState("");
  const [calls, setCalls] = useState<RecordingPickerCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);
  const headersRef = useRef<Record<string, string>>({});
  const listRef = useRef<HTMLDivElement>(null);

  const parseRecordings = (rawItems: any[]): RecordingPickerCall[] => {
    return rawItems
      .filter((c: any) => c.recordingUrl || c.recording_url)
      .map((c: any) => {
        const callType = (c.callType || c.call_type || "").toLowerCase();
        const direction = (callType === "incoming" || callType === "inbound") ? "inbound" as const : "outbound" as const;
        return {
          id: c.id,
          leadName: c.contactName || c.contact_name || null,
          phone: c.phoneNumber || c.phone_number || c.toNumber || c.to_number || c.fromNumber || c.from_number || "",
          date: c.callDate || c.call_date || c.createdAt || c.created_at || "",
          duration: c.duration || 0,
          direction,
          recordingUrl: c.recordingUrl || c.recording_url || "",
          callSid: c.callSid || c.call_sid || "",
        } satisfies RecordingPickerCall;
      });
  };

  const fetchPage = async (page: number, append = false) => {
    try {
      const { getApiUrl } = await import("@/app/_module/services/config");
      const response = await fetch(getApiUrl(`communication/calls?limit=50&page=${page}`), { headers: headersRef.current });
      if (!response.ok) { setHasMore(false); return; }
      const json = await response.json();
      const outer = json?.data || json;
      const items = outer?.items || outer?.data?.items || outer?.data || [];
      const pageItems = Array.isArray(items) ? items : [];
      const recordings = parseRecordings(pageItems);
      const totalPages = outer?.meta?.totalPages || 1;
      setHasMore(page < totalPages);
      setCalls(prev => append ? [...prev, ...recordings] : recordings);
    } catch { setHasMore(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { getSupabaseSession } = await import("@/app/_module/services/auth");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const AgencyTokenMgr = (await import("@/app/_module/services/agencyTokenManager")).default;
        const session = await getSupabaseSession();
        const agencyId = AgencyTokenMgr.getCurrentAgencyId();
        headersRef.current = {
          Authorization: `Bearer ${session?.access_token}`,
          "Content-Type": "application/json",
          ...(agencyId ? { "x-agency-id": agencyId } : {}),
        };
        await fetchPage(1);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      setLoadingMore(true);
      pageRef.current += 1;
      fetchPage(pageRef.current, true).finally(() => setLoadingMore(false));
    }
  };

  const searchLower = search.toLowerCase();
  const filtered = calls.filter((c) => {
    if (!search) return true;
    return (
      (c.leadName || "").toLowerCase().includes(searchLower) ||
      c.phone.includes(search) ||
      formatPhone(c.phone).toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="w-[420px]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700" style={{ background: "var(--primary, #3b82f6)" }}>
        <div className="flex items-center gap-2 mb-2.5">
          <Phone className="w-4 h-4 text-white/90" />
          <span className="text-sm font-bold text-white">Share Call Recording</span>
          <span className="ml-auto text-[10px] text-white/60 font-medium">{calls.length} recordings</span>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by lead name or phone..."
            className="w-full bg-white/15 backdrop-blur-sm rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-white/40 border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/25 focus:border-white/20"
            autoFocus
          />
        </div>
      </div>

      {/* List */}
      <div ref={listRef} onScroll={handleScroll} className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading recordings...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Phone className="w-8 h-8 mx-auto text-zinc-300 dark:text-zinc-600 mb-2" />
            <p className="text-sm text-zinc-400 dark:text-zinc-500">
              {search ? "No recordings match your search" : "No call recordings found"}
            </p>
          </div>
        ) : (
          filtered.map((call) => (
            <button
              key={call.id}
              onClick={() => onSelect(call)}
              className="w-full text-left px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors border-b border-zinc-100 dark:border-zinc-800 last:border-b-0 group/item"
            >
              <div className="flex items-start gap-3">
                {/* Direction icon */}
                <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5 ${
                  call.direction === "inbound"
                    ? "bg-emerald-50 dark:bg-emerald-900/20"
                    : "bg-blue-50 dark:bg-blue-900/20"
                }`}>
                  {call.direction === "inbound" ? (
                    <PhoneIncoming className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <PhoneOutgoing className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-200 truncate">
                      {call.leadName || "Unknown Caller"}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      call.direction === "inbound"
                        ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                        : "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    }`}>
                      {call.direction === "inbound" ? "IN" : "OUT"}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    {formatPhone(call.phone)}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[11px] text-zinc-400 dark:text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(call.date)}
                    </span>
                    <span className="text-[11px] text-zinc-400 dark:text-zinc-500 font-mono">
                      {formatDuration(call.duration)}
                    </span>
                  </div>
                </div>
                {/* Hover indicator */}
                <div className="flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity text-[10px] font-semibold text-blue-500 dark:text-blue-400 mt-2">
                  Share
                </div>
              </div>
            </button>
          ))
        )}
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-3 text-xs text-zinc-400">
            <Loader2 className="w-3 h-3 animate-spin" />
            Loading more...
          </div>
        )}
      </div>
    </div>
  );
}
